/**
 * Phase 125.A — Shared Accommodation / Strategy picker.
 *
 * One component, two callers (Individual Student Plan generator,
 * "Individualize this unit" overlay, and the future Classroom Support
 * generator all share this UI). Lifecycle is dead-simple:
 *
 *   1. On mount, fetch the taxonomy from `/api/accommodations`.
 *   2. Render grouped category sections with a header + helper line.
 *   3. Each entry is a checkbox row. The component is fully
 *      controlled — the parent owns `selectedIds` and `onChange`.
 *   4. Includes a free-text "Other" field that captures whatever the
 *      taxonomy doesn't yet cover. Saved as a separate `otherText`
 *      string so backend can distinguish curated vs ad-hoc.
 *
 * Privacy: the picker NEVER asks for a student name. The helper line
 * at the bottom of the component states this explicitly so any
 * teacher worried about FOIPPA can see the framing on the page.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORY_LABELS = {
  input: "Input — how the student receives instruction",
  output: "Output — how the student shows learning",
  environment: "Environment — physical & sensory setup",
  self_regulation: "Self-regulation — self-management & emotional supports",
  scaffold: "Scaffold — cognitive supports during the task",
  extension: "Extension — enrichment for advanced learners",
};

export default function AccommodationPicker({
  selectedIds = [],
  otherText = "",
  onChange,
  testIdPrefix = "accommodation",
  collapsible = false,
}) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 154 (Jul 2026) — collapsible category sections for the ISP
  // form. Empty set === all categories collapsed by default (the
  // founder-approved starting state). Only used when `collapsible`
  // is true; UnitOverlay and any other callsite that doesn't opt in
  // keeps the flat all-open render bit-for-bit unchanged.
  const [openCats, setOpenCats] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiBase()}/accommodations`, {
          credentials: "include",
        });
        if (res.status === 401 || res.status === 403) {
          // Page is auth-gated upstream; the picker just stays
          // empty and the route guard surfaces the sign-in prompt.
          if (!cancelled) setTaxonomy(null);
          return;
        }
        if (!res.ok) throw new Error(`Failed to load accommodations (${res.status})`);
        const data = await res.json();
        if (!cancelled) setTaxonomy(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Couldn't load strategies.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  // Per-category selected count. Only meaningful when `collapsible`,
  // but computed unconditionally so React memoisation keys stay
  // stable across renders.
  const countPerCat = useMemo(() => {
    const map = {};
    if (taxonomy?.categories) {
      for (const cat of taxonomy.categories) {
        map[cat.key] = (cat.entries || []).filter((e) =>
          selectedSet.has(e.id),
        ).length;
      }
    }
    return map;
  }, [taxonomy, selectedSet]);

  const toggleOpen = (key) => {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.({ selectedIds: Array.from(next), otherText });
  };

  const updateOther = (text) => {
    onChange?.({ selectedIds: Array.from(selectedSet), otherText: text });
  };

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-[#4a6984]/70 p-3"
        data-testid={`${testIdPrefix}-loading`}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading strategies…
      </div>
    );
  }
  if (!taxonomy || !taxonomy.categories?.length) {
    return (
      <div className="text-sm text-[#4a6984]/70 p-3">
        No strategies available yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-root`}>
      {taxonomy.categories.map((cat) => {
        // Extract the checkbox grid ONCE so the collapsible and flat
        // branches share it — the fix must not duplicate the entry
        // markup (founder rule: one shared change, not six copies).
        const entriesGrid = (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {cat.entries.map((entry) => {
              const checked = selectedSet.has(entry.id);
              return (
                <label
                  key={entry.id}
                  className={`flex items-start gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${
                    checked
                      ? "bg-[#96ded1]/20 text-[#1c2d3d]"
                      : "text-[#1c2d3d] hover:bg-[#96ded1]/10"
                  }`}
                  data-testid={`${testIdPrefix}-entry-${entry.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(entry.id)}
                    data-testid={`${testIdPrefix}-checkbox-${entry.id}`}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/30 text-[#96ded1] focus:ring-[#96ded1]"
                  />
                  <span className="flex-1">
                    <span className="font-bold">{entry.label}</span>
                    <span className="block text-[10px] text-[#4a6984]/70 mt-0.5 leading-snug">
                      {entry.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        );

        if (collapsible) {
          const isOpen = openCats.has(cat.key);
          const catCount = countPerCat[cat.key] || 0;
          const panelId = `${testIdPrefix}-cat-panel-${cat.key}`;
          return (
            <section
              key={cat.key}
              className="border border-[#4a6984]/15 rounded-md overflow-hidden"
              data-testid={`${testIdPrefix}-cat-${cat.key}`}
            >
              <button
                type="button"
                onClick={() => toggleOpen(cat.key)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                data-testid={`${testIdPrefix}-cat-toggle-${cat.key}`}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-[#96ded1]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1] transition-colors"
              >
                <span className="flex-1 text-[11px] font-extrabold uppercase tracking-wide text-[#1c2d3d]">
                  {CATEGORY_LABELS[cat.key] || cat.key}
                </span>
                {catCount > 0 ? (
                  <span
                    data-testid={`${testIdPrefix}-cat-count-${cat.key}`}
                    className="inline-flex items-center rounded-full bg-[#96ded1]/40 px-2 py-0.5 text-[10px] font-black text-[#1c2d3d]"
                  >
                    {catCount} selected
                  </span>
                ) : null}
                <ChevronDown
                  aria-hidden="true"
                  className={`h-4 w-4 text-[#4a6984] transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                id={panelId}
                hidden={!isOpen}
                className={isOpen ? "px-3 pb-3" : ""}
              >
                <p className="text-xs text-[#4a6984]/70 mb-2">{cat.subtitle}</p>
                {entriesGrid}
              </div>
            </section>
          );
        }

        return (
          <fieldset
            key={cat.key}
            className="border border-[#4a6984]/15 rounded-md p-3"
            data-testid={`${testIdPrefix}-cat-${cat.key}`}
          >
            <legend className="px-2 text-[11px] font-extrabold uppercase tracking-wide text-[#1c2d3d]">
              {CATEGORY_LABELS[cat.key] || cat.key}
            </legend>
            <p className="text-xs text-[#4a6984]/70 mb-2">{cat.subtitle}</p>
            {entriesGrid}
          </fieldset>
        );
      })}

      <fieldset className="border border-[#4a6984]/15 rounded-md p-3">
        <legend className="px-2 text-[11px] font-extrabold uppercase tracking-wide text-[#1c2d3d]">
          Other strategies
        </legend>
        <p className="text-xs text-[#4a6984]/70 mb-2">
          Add anything else relevant to this student. No student name needed —
          describe the support only.
        </p>
        <textarea
          value={otherText}
          onChange={(e) => updateOther(e.target.value)}
          data-testid={`${testIdPrefix}-other`}
          rows={3}
          placeholder="e.g., Pair with a buddy for the read-aloud portion; allow a stand-up workstation during writing."
          className="w-full px-3 py-2 rounded-md border border-[#4a6984]/20 text-xs focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
        />
      </fieldset>

      <p className="text-[11px] text-[#4a6984]/60 italic">
        No student name needed — describe the goal and supports only.
      </p>
    </div>
  );
}
